import React from 'react';
import { IonButton} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import EbioroLoadingLogo from '../EbioroLoading/EbioroLoadingLogo';
import { TransactionStatus } from '../../services/transaction/transactionTracker';
import './TransactionStatusDisplay.css';

interface TransactionStatusDisplayProps {
  /**
   * Current transaction status
   */
  status: TransactionStatus | null;
  
  /**
   * Status message to display
   */
  message?: string | null;
  
  /**
   * Whether transaction is being tracked
   */
  isTracking: boolean;
  
  /**
   * Error message if any
   */
  error?: string | null;
  
  /**
   * Custom messages for different states
   */
  customMessages?: {
    pending?: string;
    surgePricing?: string;
    processing?: string;
    completed?: string;
    error?: string;
  };
  
  /**
   * Custom loading component
   */
  LoadingComponent?: React.ComponentType<{ message: string }>;
  
  /**
   * Callbacks for different states
   */
  onRetry?: () => void;
  onSuccess?: () => void;
  onCancel?: () => void;
  
  /**
   * Whether to show action buttons
   */
  showButtons?: boolean;
  
  /**
   * Custom styling classes
   */
  className?: string;
}

const TransactionStatusDisplay: React.FC<TransactionStatusDisplayProps> = ({
  status,
  message,
  isTracking,
  error,
  customMessages = {},
  LoadingComponent,
  onRetry,
  onSuccess,
  onCancel,
  showButtons = true,
  className = ''
}) => {
  const { t } = useTranslation();

  const getStatusMessage = (): string => {
    if (error) {
      return customMessages.error || error;
    }

    if (message) {
      return message;
    }

    switch (status) {
      case 'pending':
        return customMessages.pending || t('send.page.WAIT-MESSAGE-MAIN');
      case 'surge_pricing':
        return customMessages.surgePricing || t('send.page.WAIT-SURGE-PRICING');
      case 'processing':
        return customMessages.processing || t('Transaction is being processed');
      case 'completed':
        return customMessages.completed || t('Transaction completed successfully');
      case 'error':
        return customMessages.error || t('application.uncaught.ERROR');
      default:
        return isTracking ? t('Checking transaction status...') : t('Ready');
    }
  };

  const renderLoadingState = () => {
    const statusMessage = getStatusMessage();
    
    if (LoadingComponent) {
      return <LoadingComponent message={statusMessage} />;
    }

    return (
      <div className="ion-text-center">
        <div className="transaction-status-loading">
          <EbioroLoadingLogo
            mainText={statusMessage}
            mainTextColor="primary"
            subText={t('send.page.WAIT-MESSAGE-SUB') as string}
            subTextColor="primary"
          />
        </div>
      </div>
    );
  };

  const renderSuccessState = () => (
    <div className={`ion-text-center transaction-status-success ${className}`}>
      <div className="success-icon-container">
        <div className="success-icon">
          <svg fill="currentColor" viewBox="0 0 20 20" className="w-16 h-16 text-green-500">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
      <h2 className="success-title">{t('Success!')}</h2>
      <p className="success-message">{getStatusMessage()}</p>
      
      {showButtons && onSuccess && (
        <IonButton 
          expand="block" 
          color="success" 
          onClick={onSuccess}
          className="mt-4"
        >
          {t('Continue')}
        </IonButton>
      )}
    </div>
  );

  const renderErrorState = () => (
    <div className={`ion-text-center transaction-status-error ${className}`}>
      <div className="error-icon-container">
        <div className="error-icon">
          <svg fill="currentColor" viewBox="0 0 20 20" className="w-16 h-16 text-red-500">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
      <h2 className="error-title">{t('Error')}</h2>
      <p className="error-message">{getStatusMessage()}</p>
      
      {showButtons && (
        <div className="error-buttons">
          {onRetry && (
            <IonButton 
              expand="block" 
              color="primary" 
              onClick={onRetry}
              className="mt-4"
            >
              {t('Try Again')}
            </IonButton>
          )}
          {onCancel && (
            <IonButton 
              expand="block" 
              fill="outline" 
              color="medium" 
              onClick={onCancel}
              className="mt-2"
            >
              {t('Cancel')}
            </IonButton>
          )}
        </div>
      )}
    </div>
  );

  // Show loading state for pending, surge_pricing, or when actively tracking
  if (isTracking || ['pending', 'surge_pricing', 'processing'].includes(status as string)) {
    return renderLoadingState();
  }

  // Show success state
  if (status === 'completed') {
    return renderSuccessState();
  }

  // Show error state
  if (status === 'error' || error) {
    return renderErrorState();
  }

  // Default state - not tracking
  return null;
};

export default TransactionStatusDisplay;